import React, { createContext, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import { CntStws, Survey, SurveyTimeWindow } from "../types/surveyTypes";
import { ReportEntry } from "../types/reportTypes";
import { IUserInfo } from "../types/authTypes";
import {
  getCurrentSurveyTimeWindow,
  getPatientReports,
  patientGetSurveyTimeWindows,
} from "../services/getFunctions";
import { useAlertHelpers } from "../helper/Hooks/useAlertHelpers";
import { showAlertModal } from "../helper/globalAlert";
import { toErrorType } from "../helper/alertBoxes";
import { getGermanTodayIso, formatCalendarDate } from "../helper/dateHelpers";

export interface HomePatientContextState {
  content?: string;
  stws: SurveyTimeWindow[];
  survey: Survey;
  reports: ReportEntry[];
  currentSTW: SurveyTimeWindow;
  today: string;
  cntStws: CntStws;
  modalOpen: boolean;
  remainingTime: number;
  value: string;
  userInfo?: IUserInfo;
}

export const initialState: HomePatientContextState = {
  content: "",
  survey: {
    dimension: {
      title: "",
      connector: "",
      footer: "",
      id: "",
      paragraph: "",
    },
    items: [],
    survey_session_id: 0,
  },
  stws: [],
  reports: [],
  currentSTW: {
    type: "",
    start: "",
    blocking: "",
    end: "",
    survey: {
      started: false,
      finished: false,
    },
  },
  today: getGermanTodayIso(),
  value: "",
  cntStws: {
    current: 0,
    completed: 0,
    planned: 0,
  },
  modalOpen: false,
  remainingTime: 0,
};

export interface HomePatientContextType {
  state: HomePatientContextState;
  setState: (state: HomePatientContextState) => void;
  openSurvey: () => void;
  openReport: (
    event: React.MouseEvent<HTMLButtonElement>,
    reportIndex?: number,
  ) => void;
}

export const HomePatientContext = createContext<HomePatientContextType>({
  state: initialState,
  setState: () => {},
  openSurvey: () => {},
  openReport: () => {},
});

function useHomePatientState(extraData: Partial<HomePatientContextState>) {
  const [state, setState] = React.useState({
    ...initialState,
    ...(extraData ?? {}),
  });

  const { reportGetPatientReportsError, reportGetStwsError } =
    useAlertHelpers();

  const cntStws = useCallback(
    (data: SurveyTimeWindow[]) => {
      const cntStws = {
        current: 0,
        completed: 0,
        planned: 0,
      };

      const stws = [...data];

      for (let i = 0; i < data.length; i++) {
        const start = data[i].start;
        const end = data[i].end;
        const start_date = formatCalendarDate(start);
        const end_date = formatCalendarDate(end);

        stws[i] = {
          ...data[i],
          start_show: start_date,
          end_show: end_date,
        };
      }

      for (let j = 0; j < data.length; j++) {
        if (data[j].start <= state.today && data[j].end >= state.today) {
          cntStws.current++;
        } else if (data[j].end < state.today) {
          cntStws.completed++;
        } else if (data[j].start > state.today) {
          cntStws.planned++;
        }
      }
      return {
        stws,
        cntStws,
      };
    },
    [state.today],
  );

  /**
   *This function calls `patientGetSurveyTimeWindows` to get survey time windows (stws) data.
   *If successful, it processes the retrieved data using `cntStws` and returns the result.
   *If an error occurs, it reports the error using `reportGetStwsError` and returns the initial state.
   */
  const tryPatientGetSurveyTimeWindows = useCallback(async () => {
    try {
      const result = await patientGetSurveyTimeWindows();
      return cntStws(result.stws);
    } catch (error) {
      reportGetStwsError(toErrorType(error));
      return {
        stws: initialState.stws,
        cntStws: initialState.cntStws,
      };
    }
  }, [cntStws, reportGetStwsError]);

  /**
   * `useEffect` hook that asynchronously retrieves various pieces of survey-related data
   * when the component mounts and updates the state with the results.
   *
   *  Executes an immediately invoked asynchronous function.
   *  Retrieves data by calling four functions concurrently: `tryPatientGetSurveyTimeWindows`,
   *    `tryGetCurrentSurveyTimeWindow`, `tryGetSurvey`, and `tryGetPatientReports`.
   *  Combines the results into a single object, `resultState`.
   *  Updates the component's state with the combined results, merging them with the existing state.
   */
  useEffect(() => {
    let isMounted = true;

    /**
     * This function calls `getCurrentSurveyTimeWindow` to get the current survey time window data.
     * If successful, it returns the result as an object with `currentSTW` property.
     * If an error occurs, it reports the error using `reportGetStwsError` and returns the initial state.
     */
    const tryGetCurrentSurveyTimeWindow = async () => {
      try {
        const result = await getCurrentSurveyTimeWindow();
        return { currentSTW: result };
      } catch (error) {
        reportGetStwsError(toErrorType(error));
        return { currentSTW: initialState.currentSTW };
      }
    };

    /**
     *This function calls `getPatientReports` to get the patient reports data.
     * If successful, it returns the reports as an object with a `reports` property containing the report entries.
     * If an error occurs, it reports the error using `reportGetPatientReportsError` and returns the initial state.
     */
    const tryGetPatientReports = async () => {
      try {
        const result = await getPatientReports();
        return { reports: result.report_entries };
      } catch (error) {
        reportGetPatientReportsError(toErrorType(error));
        return { reports: initialState.reports };
      }
    };

    const fetchData = async () => {
      try {
        const results = await Promise.all([
          tryPatientGetSurveyTimeWindows(),
          tryGetCurrentSurveyTimeWindow(),
          tryGetPatientReports(),
        ]);

        if (isMounted) {
          const resultState = {};
          for (const r of results) {
            Object.assign(resultState, r);
          }
          setState((prev) => ({ ...prev, ...resultState }));
        }
      } catch (error) {
        console.error("Error fetching patient data:", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    tryPatientGetSurveyTimeWindows,
    reportGetStwsError,
    reportGetPatientReportsError,
  ]);

  return { state, setState };
}

/**
 * To manage state and context for home patient data.
 * The child component to be wrapped by this provider.
 * The provider component that supplies state and context to its children.
 */

const HomePatientProvider = ({ children }) => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();

  const { state, setState } = useHomePatientState({
    userInfo: authState.userInfo,
  });

  /**Open patients survey. */
  const openSurvey = useCallback(() => {
    navigate("/patients/survey");
  }, [navigate]);

  //handler function for reuse
  const handleReportOpen = useCallback(
    (index: number) => {
      if (!state.reports || !Array.isArray(state.reports)) {
        console.error("Report data not loaded or in an incorrect format");
        showAlertModal("Report data unavailable");
        return;
      }

      //check index range
      if (index < 0 || index >= state.reports.length) {
        console.error(
          `Report index out of range: ${index}, available reports: ${state.reports.length}`,
        );
        showAlertModal("Requested report does not exist");
        return;
      }

      //check the report object
      const report = state.reports[index];
      if (!report) {
        console.error(`Report at index ${index} is empty`);
        showAlertModal("Report data is corrupted");
        return;
      }

      //check data time
      if (!report.started_time) {
        console.error("Report is missing start time data:", report);
        showAlertModal("Report data is incomplete");
        return;
      }

      try {
        // date format conversion
        const started_time_format = formatCalendarDate(report.started_time);

        // Navigate to the report page
        navigate("/patients/report", {
          state: {
            survey_session_id: report.survey_session_id,
            stw_type: report.stw_type,
            started_time: report.started_time,
            formattedDate: started_time_format,
          },
        });
      } catch (error) {
        console.error("Error while processing report data:", error);
        showAlertModal("An error occurred while processing the report data");
      }
    },
    [state.reports, navigate],
  );

  /**Open patients report. */
  const openReport = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, reportIndex?: number) => {
      event.preventDefault();

      // directly use input index
      if (reportIndex !== undefined) {
        handleReportOpen(reportIndex);
        return;
      }

      // Use currentTarget to get the element the event handler is attached to
      const buttonElement = event.currentTarget;
      // Attempt to find the grandparent element with an id
      const indexStr = buttonElement.getAttribute("data-report-index");

      if (indexStr === null) {
        console.error("Unable to get report index");
        showAlertModal("Unable to open report, index information is missing");
        return;
      }

      const index = parseInt(indexStr, 10);
      handleReportOpen(index);
    },
    [handleReportOpen],
  );

  return (
    <HomePatientContext.Provider
      value={{
        state,
        setState,
        openSurvey,
        openReport,
      }}
    >
      {children}
    </HomePatientContext.Provider>
  );
};

export default HomePatientProvider;
